/*
 * Copyright (C) 2020 Airfordable, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import './animation.css';

import { useLazyQuery } from '@apollo/client';
import { styled, useTheme } from '@material-ui/core';
import { graphql } from 'gatsby';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import gql from 'graphql-tag';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';

import googlePlay from './android-button.png';
import appStore from './apple-button.png';
import usFlag from './us-flag.png';
import {
  Button,
  FormInput,
  TitleHighlight,
  Typography,
  useAlert,
} from 'components/UI';
import { AF_BETA_ORIGIN, isProduction } from 'utils/constants';
import deviceDetection from 'utils/deviceDetection';
import { Breakpoints } from 'utils/enums';
import { formatPhoneNumber } from 'utils/formatPhoneNumbers';
import { useDynamicDownloadLink } from 'utils/useDynamicDownloadLink';

// prettier-ignore
const PHONE_QUERY = gql`
  query sendCabinMobileAppStoreLink($phoneNumber: String!, $dynamicStoreLink: String) {
    sendCabinMobileAppStoreLink(phoneNumber: $phoneNumber, dynamicStoreLink: $dynamicStoreLink)
  }
`;

const Title = styled(TitleHighlight)(({ theme }) => ({
  '& span': {
    display: 'block',
    fontSize: '1.8em',
    margin: '0 auto',
  },
  ...theme.typography.h4,
  fontWeight: 500,
  marginBottom: theme.spacing(4),
  textAlign: 'center',

  [theme.breakpoints.up(Breakpoints.Md)]: {
    '& span': {
      display: 'inline-block',
      fontSize: '2em',
      margin: 0,
    },

    marginBottom: theme.spacing(6),
    textAlign: 'left',
    ...theme.typography.h3,
  },
}));

const Text = styled(Typography)(({ theme }) => ({
  textAlign: 'center',

  [theme.breakpoints.up(Breakpoints.Md)]: {
    textAlign: 'left',
    ...theme.typography.body1,
  },
}));

const QRCodeDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 500,
}));

const Skeleton = styled('div')(() => ({
  alignItems: 'center',
  backgroundColor: 'lightgray',
  borderRadius: '5px',
  display: 'flex',
  height: '130px',
  justifyContent: 'center',
  margin: '10px',
  width: '130px',
}));

const Spinner = styled('div')(() => ({
  animation: 'spin 2s linear infinite',
  border: '4px solid #f3f3f3',
  borderRadius: '50%',
  borderTop: '4px solid #111',
  height: '60px',
  width: '60px',
}));

const ColImages = styled('div')(({ theme }) => ({
  '& > *:nth-child(1)': {
    marginRight: '2em',
    marginTop: theme.spacing(10),
    width: '50%',
    [theme.breakpoints.up(Breakpoints.Md)]: {
      marginTop: theme.spacing(-46),
    },
  },
  '& > *:nth-child(2)': {
    marginTop: theme.spacing(3),
    width: '60%',
    [theme.breakpoints.up(Breakpoints.Md)]: {
      marginTop: theme.spacing(-28),
    },
  },
  display: 'flex',
  marginRight: '-70%',
  order: 2,
  [theme.breakpoints.up(Breakpoints.Md)]: {
    marginRight: 0,
    order: 'unset',
  },
}));

const Content = styled('div')(({ theme }) => ({
  [theme.breakpoints.up(Breakpoints.Lg)]: {
    padding: theme.spacing(0, 16),
  },
}));

const QRCodeSection = styled('div')(({ theme }) => ({
  '& div': {
    alignItems: 'center',
    display: 'flex',
  },
  '& svg': {
    padding: theme.spacing(2.5, 2.5),
  },
  border: `0.5px solid ${theme.palette.secondary.light}`,
  borderRadius: theme.borderRadius[1],
  display: 'none',
  gap: theme.spacing(2),
  margin: theme.spacing(3, 0),
  [theme.breakpoints.up(Breakpoints.Md)]: {
    display: 'flex',
  },
}));

const Form = styled('form')(({ theme }) => ({
  '& .download-button': {
    borderRadius: theme.borderRadius[1],
    lineHeight: 0.7,
    padding: theme.spacing(4, 2),
  },
  display: 'none',
  paddingBottom: theme.spacing(8),
  [theme.breakpoints.up(Breakpoints.Md)]: {
    display: 'block',
  },
}));

const FormCaption = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(12),
  marginBottom: theme.spacing(1),
}));

const ScreenShotSection = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up(Breakpoints.Md)]: {
    display: 'block',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(8, 0),
  },
}));

const ScreenShotSectionMobile = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(8, 0),
  [theme.breakpoints.up(Breakpoints.Md)]: {
    display: 'none',
  },
}));

const DownloadSection = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up(Breakpoints.Md)]: {
    display: 'block',
  },
}));

const DownloadSectionMobile = styled('div')(({ theme }) => ({
  display: 'block',
  [theme.breakpoints.up(Breakpoints.Md)]: {
    display: 'none',
  },
}));

const DownloadCaption = styled(Typography)(({ theme }) => ({
  display: 'block',
  fontWeight: 500,
  textAlign: 'center',
  textTransform: 'uppercase',
  [theme.breakpoints.up(Breakpoints.Md)]: {
    textAlign: 'left',
  },
}));

const DownloadImageList = styled('div')(({ theme }) => ({
  '& > *': {
    '& img': {
      [theme.breakpoints.up(Breakpoints.Md)]: {
        height: theme.spacing(14.8),
      },
      [theme.breakpoints.down(Breakpoints.Md)]: {
        height: theme.spacing(11),
      },
    },
    margin: theme.spacing(0, 1.5),
    [theme.breakpoints.up(Breakpoints.Md)]: {
      marginLeft: 0,
      marginRight: theme.spacing(3),
    },
  },
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(3),
  width: '100%',
  [theme.breakpoints.up(Breakpoints.Md)]: {
    justifyContent: 'flex-start',
  },
}));

type ChildrenProps = {
  showLinkToBeta?: boolean;
  render(args: {
    mobileButtons: React.ReactElement;
    content: React.ReactElement;
    images: React.ReactElement;
  }): void;
};

const DownloadCabinButtons: React.FC<
  DownloadProps & { dynamicLink: string | undefined }
> = ({ content, dynamicLink }) => {
  const { unKnownDevice, isIos, isAndroid } = deviceDetection();
  const shouldUseDynamicLink = isProduction && !!dynamicLink;

  return (
    <DownloadImageList>
      {(isIos || unKnownDevice) && (
        <a
          href={shouldUseDynamicLink ? dynamicLink : content.links.appStore}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={appStore} alt="Download on the App Store" />
        </a>
      )}

      {(isAndroid || unKnownDevice) && (
        <a
          href={shouldUseDynamicLink ? dynamicLink : content.links.googlePlay}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={googlePlay} alt="Get it on Google Play" />
        </a>
      )}
    </DownloadImageList>
  );
};

const Download: React.FC<DownloadProps & ChildrenProps> = ({
  content,
  render,
  showLinkToBeta = true,
}) => {
  const theme = useTheme();
  const [value, setValue] = useState('');
  const alert = useAlert();

  const [fetchSms, { loading, error, called }] = useLazyQuery(PHONE_QUERY);

  const dynamicLinkValue = useDynamicDownloadLink();

  const showSMSForm = useFeatureFlagEnabled('runway-download-sms-form');

  useEffect(() => {
    if (!loading && !error && called) {
      alert.success(content.success);
      setValue('');
    }

    if (!loading && error && called) {
      alert.error(content.error);
    }
  }, [alert, called, content.error, content.success, error, loading]);

  const handleSubmit = () => {
    fetchSms({
      variables: {
        dynamicStoreLink: dynamicLinkValue,
        phoneNumber: formatPhoneNumber('1' + value),
      },
    });
  };

  const screenShotChildren = (
    <>
      <hr style={{ margin: theme.spacing(8, 0) }} />
      <DownloadCaption variant="caption" color="textSecondary" gutterBottom>
        Need to go back to our previous site to book with screenshots?
      </DownloadCaption>
      <Button to={AF_BETA_ORIGIN}>Book using a screenshot</Button>
    </>
  );

  return (
    <>
      {render({
        content: (
          <div>
            <Content>
              <Title as="h2">{content.title}</Title>
              <Text variant="body2">{content.text}</Text>

              <QRCodeSection>
                {dynamicLinkValue ? (
                  <QRCode size={150} value={dynamicLinkValue} />
                ) : (
                  <Skeleton>
                    <Spinner />
                  </Skeleton>
                )}
                <div>
                  <QRCodeDescription>
                    {content.qrCode.description}
                  </QRCodeDescription>
                </div>
              </QRCodeSection>

              {showSMSForm && (
                <Form>
                  <FormInput
                    onChange={({
                      target,
                    }: React.ChangeEvent<HTMLInputElement>) =>
                      setValue(target.value)
                    }
                    name="phone-number"
                    placeholder="(000) 000 0000"
                    label={content.form.label}
                    mask={[
                      '(',
                      /[1-9]/,
                      /\d/,
                      /\d/,
                      ')',
                      ' ',
                      /\d/,
                      /\d/,
                      /\d/,
                      '-',
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                    ]}
                    value={value}
                    adornment={
                      <span>
                        <img src={usFlag} alt="USA" width="24" />
                      </span>
                    }
                  >
                    <Button
                      className="download-button"
                      disabled={value.length < 1}
                      onClick={handleSubmit}
                      type="button"
                    >
                      {content.form.button}
                    </Button>
                  </FormInput>

                  <FormCaption>{content.form.postFormText}</FormCaption>
                </Form>
              )}

              <DownloadSection>
                <DownloadCaption variant="caption" color="textSecondary">
                  {content.links.label}
                </DownloadCaption>

                <DownloadImageList>
                  {content.links.appStore && (
                    <a
                      href={isProduction ? content.links.appStore : '/download'}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src={appStore} alt="Download on the App Store" />
                    </a>
                  )}

                  {content.links.googlePlay && (
                    <a
                      href={
                        isProduction ? content.links.googlePlay : '/download'
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src={googlePlay} alt="Get it on Google Play" />
                    </a>
                  )}
                </DownloadImageList>
              </DownloadSection>
              {showLinkToBeta && (
                <ScreenShotSection>{screenShotChildren}</ScreenShotSection>
              )}
            </Content>
          </div>
        ),
        images: (
          <ColImages className="col-images">
            {content.images.map((item, index) => (
              <div key={`image-${index}`}>
                <GatsbyImage
                  alt=""
                  image={item.childImageSharp.gatsbyImageData}
                />
              </div>
            ))}
          </ColImages>
        ),
        mobileButtons: (
          <>
            <DownloadSectionMobile>
              <DownloadCaption variant="caption" color="textSecondary">
                {content.links.label}
              </DownloadCaption>

              <DownloadCabinButtons
                content={content}
                dynamicLink={dynamicLinkValue}
              />
            </DownloadSectionMobile>

            {showLinkToBeta && (
              <ScreenShotSectionMobile>
                {screenShotChildren}
              </ScreenShotSectionMobile>
            )}
          </>
        ),
      })}
    </>
  );
};

export interface DownloadProps {
  content: {
    title: string;
    text: string;
    success: string;
    error: string;
    qrCode: {
      description: string;
    };
    images: {
      childImageSharp: {
        gatsbyImageData: IGatsbyImageData;
      };
    }[];
    form: {
      label: string;
      button: string;
      postFormText: string;
    };
    links: {
      label: string;
      appStore: string;
      googlePlay: string;
    };
  };
}

const query = graphql`
  fragment DownloadContent on DownloadYaml {
    metadata {
      title
      description
    }
    content {
      title
      text
      success
      error
      qrCode {
        description
      }
      images {
        childImageSharp {
          gatsbyImageData
        }
      }
      form {
        label
        button
        postFormText
      }
      links {
        label
        appStore
        googlePlay
      }
    }
  }
`;

export { Download, DownloadCabinButtons, query };
